import React, { useEffect, useState } from 'react';
import { editType } from 'app/constants';
import { GlanceFieldValue } from '../OrderDetailsDesign';
import { FormControl, FormControlLabel, Grid, InputAdornment, Radio, RadioGroup, Switch, TextField } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { PickList } from './PickList';
import { DateEditor } from './DateEditor';
import { IOrderDetails } from 'app/models/Orders/OrderDetails';
import { FileOutReason } from './FileOutReason';
interface props {
  title: string;
  value: string;
  name: string;
  editable?: boolean;
  editTypeValue?: { type: editType; textFieldType?: string };
  handleOrderUpdate: (property: string, value: any) => void;
  editedOrder: IOrderDetails;
  disableEdit: boolean;
  handlePositionUpdate?: (event: any) => void;
  errorField?: boolean;
  editOrderPage?: boolean;
  hideHelperText?: boolean;
}

const useStyles = makeStyles()(theme => ({
  root: {
    '& .MuiFilledInput-input': {
      padding: '3px 4px 2px',
    },
    '& .MuiInputBase-input': {
      fontSize: 14,
    },
    '& .MuiSelect-filled': {
      padding: '2px 4px 2px',
    },
    '& .MuiTypography-body1': {
      fontSize: 14,
    },
    input: {
      height: 50,
    },
    height: 25,
    width: 115,
  },
  toggleLabel: {
    fontSize: 14,
    color: '#333333',
  },
  toggleWrap: {
    marginLeft: -10,
    marginTop: -8,
    display: 'inline-block',
    verticalAlign: 'top',
  },
  input: {
    '& input::-webkit-clear-button, & input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
      display: 'none',
    },
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0,
    fontSize: 12,
    height: 25,
  },
  responsiveValue: {
    [theme.breakpoints.down('lg')]: {
      flexDirection: 'column',
    },
    paddingLeft: 15,
  },
}));

export const GlanceValue = (glanceValues: props) => {
  const {
    title,
    value,
    editable,
    editTypeValue,
    handleOrderUpdate,
    hideHelperText = false,
    name,
    editedOrder,
    disableEdit,
    errorField = false,
    editOrderPage = false,
  } = glanceValues;
  const { classes } = useStyles();
  const [elementValue, setElementValue] = useState<{ itemValue: any | null }>({
    itemValue: value === '-' ? '' : value,
  });
  const { t } = useTranslation();

  const validateControl = value => {
    if ((value <= 99 && value >= 1) || value === '') return value;
    return editedOrder.numOfWeeks;
  };

  const handleTextFieldChange = event => {
    let value = title === 'Placement Length' ? validateControl(event.target.value) : event.target.value;
    if (value === '') value = null;
    if (editTypeValue?.textFieldType === 'number') value = parseInt(value);
    setElementValue({ itemValue: value });
    handleOrderUpdate(name, value);
  };

  useEffect(() => {
    setElementValue({ itemValue: value === '-' ? '' : value });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleSwitchChange = event => {
    setElementValue({
      itemValue: event.target.checked ? 'Yes' : 'No',
    });
    handleOrderUpdate(name, event.target.checked);
  };

  const handleRadioChange = event => {
    const radioValue = event.target.value === 'true';
    setElementValue({
      itemValue: radioValue,
    });
    handleOrderUpdate(name, radioValue);
  };

  const handleFileOutChange = event => {
    setElementValue({ itemValue: event.target.value });
    handleOrderUpdate(name, event.target.value);
  };

  const handleChange = (property: string, value: any) => {
    setElementValue({ itemValue: value });
    handleOrderUpdate(property, value);
  };

  const element = (editElementType: { type: editType; textFieldType?: string }, title: string) => {
    switch (editElementType.type) {
      case 'textField':
        return (
          <>
            <TextField
              variant="filled"
              disabled={disableEdit}
              value={elementValue.itemValue}
              onChange={handleTextFieldChange}
              type={editElementType.textFieldType}
              className={classes.root}
              autoComplete="off"
              name={title}
              inputProps={{
                min: 0,
                'aria-label': 'test-textField',
              }}
              InputProps={{
                className: classes.input,
                disableUnderline: true,
                endAdornment: (
                  <InputAdornment
                    style={{ display: `${title === 'Placement Length' ? 'visible' : 'none'}` }}
                    position="end"
                  >
                    {t(' Weeks')}
                  </InputAdornment>
                ),
              }}
            />
          </>
        );
      case 'picklist':
        return (
          <PickList
            title={title}
            value={elementValue.itemValue}
            handleChange={handleChange}
            name={name}
            disableEdit={disableEdit}
          />
        );
      case 'switch':
        return (
          <span className={classes.toggleWrap}>
            <Switch
              onChange={handleSwitchChange}
              checked={elementValue.itemValue === 'Yes' || elementValue.itemValue === true}
              color="primary"
              name={title}
              disabled={disableEdit}
            />
            <span className={classes.toggleLabel}>
              {elementValue.itemValue === ''
                ? 'No'
                : elementValue.itemValue === true || elementValue.itemValue === 'Yes'
                ? 'Yes'
                : 'No'}
            </span>
          </span>
        );
      case 'datePicker':
        return (
          <DateEditor
            title={title}
            value={elementValue.itemValue}
            handleChange={handleChange}
            name={name}
            disableEdit={disableEdit}
            orderDetails={editedOrder}
          />
        );
      case 'radio':
        return (
          <span className={classes.toggleWrap}>
            <FormControl disabled={disableEdit}>
              <RadioGroup row value={elementValue?.itemValue?.toString()} name={name} onChange={handleRadioChange}>
                <FormControlLabel
                  value={`true`}
                  control={<Radio disableFocusRipple disableRipple disableTouchRipple />}
                  label={'Decline Files'}
                />
                <FormControlLabel
                  value={`false`}
                  control={<Radio disableFocusRipple disableRipple disableTouchRipple />}
                  label={'Keep Files'}
                />
              </RadioGroup>
            </FormControl>
          </span>
        );
      case 'fileOutReason':
        return (
          <FileOutReason
            editOrderPage={editOrderPage}
            errorField={errorField}
            title={title}
            name={name}
            disableEdit={disableEdit}
            value={elementValue.itemValue}
            handleFileOutChange={handleFileOutChange}
            hideHelperText={hideHelperText}
          />
        );
    }
  };
  return editable && editTypeValue?.type ? (
    <>
      <Grid container direction="column" data-testid="test-atAGlance">
        <Grid item xs={12} className={classes.responsiveValue}>
          {element(editTypeValue, title)}
        </Grid>
      </Grid>
    </>
  ) : (
    <GlanceFieldValue title={title}>{value}</GlanceFieldValue>
  );
};
